/**
 * App settings model (IMPL-20). Kept as plain data + pure helpers so the settings UI stays
 * presentational and testable without a DOM: the app owns persistence (localStorage) and the side
 * effects (applying the theme vars, injecting custom CSS), while this module defines the shape,
 * the defaults, and the small pure resolvers the UI and the app both need.
 */
import type { ThemeName } from './theme.js';

/** What the user picks; `system` follows the OS via `prefers-color-scheme`. */
export type ThemeMode = 'light' | 'dark' | 'system';

export interface LapoSettings {
  /** Display name for the local user (shown in chrome, used to attribute edits). */
  displayName: string;
  /** Theme preference. `system` defers to the OS. */
  theme: ThemeMode;
  /** Drop the accent hue (for reflective grayscale displays like the Daylight). */
  grayscale: boolean;
  /** User CSS injected over the editor/renderer surfaces. */
  customCss: string;
}

// Note: linked accounts (Google Gmail/Calendar, Todoist) are NOT stored here — they live server-side
// under the signed-in identity, with their tokens KMS-encrypted, and are fetched via `/me`. Settings
// is only client-local preferences.

export const DEFAULT_SETTINGS: LapoSettings = {
  displayName: '',
  theme: 'system',
  grayscale: false,
  customCss: '',
};

/** Stable id for each section in the left rail. */
export type SettingsSectionId =
  | 'account'
  | 'general'
  | 'google'
  | 'models'
  | 'automations'
  | 'triage'
  | 'scratchpad'
  | 'appearance';

export interface SettingsSection {
  id: SettingsSectionId;
  label: string;
  /** Google Material Symbols icon name for the rail. */
  icon: string;
}

/** Left-rail sections, in display order. Icons are Material Symbols names. */
export const SETTINGS_SECTIONS: SettingsSection[] = [
  { id: 'account', label: 'Account', icon: 'account_circle' },
  { id: 'general', label: 'General', icon: 'tune' },
  { id: 'google', label: 'Google', icon: 'account_circle' },
  { id: 'models', label: 'Models', icon: 'smart_toy' },
  { id: 'automations', label: 'Automations', icon: 'bolt' },
  { id: 'triage', label: 'Triage rules', icon: 'inbox' },
  { id: 'scratchpad', label: 'Scratchpad', icon: 'sticky_note_2' },
  { id: 'appearance', label: 'Appearance', icon: 'palette' },
];

/**
 * Rename-map text format for the Daily Agenda editor: one `old => new` per line. Pure so the
 * settings UI stays presentational and the parsing is unit-tested. Blank lines and lines without a
 * `=>` are ignored; whitespace around each side is trimmed.
 */
export function parseRenameMap(text: string): Record<string, string> {
  const out: Record<string, string> = {};
  for (const line of text.split('\n')) {
    const idx = line.indexOf('=>');
    if (idx === -1) continue;
    const from = line.slice(0, idx).trim();
    const to = line.slice(idx + 2).trim();
    if (from) out[from] = to;
  }
  return out;
}

export function renameMapToText(map: Record<string, string>): string {
  return Object.entries(map)
    .map(([from, to]) => `${from} => ${to}`)
    .join('\n');
}

/** Newline-separated list editor: split, trim, drop blanks. */
export function parseLines(text: string): string[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
}

export function linesToText(list: string[]): string {
  return list.join('\n');
}

/** Resolve the concrete theme to render, given the mode and the OS preference. */
export function resolveThemeName(mode: ThemeMode, systemPrefersDark: boolean): ThemeName {
  if (mode === 'system') return systemPrefersDark ? 'dark' : 'light';
  return mode;
}

/** Merge a stored (possibly partial / older) settings blob over the defaults. */
export function normalizeSettings(raw: unknown): LapoSettings {
  if (!raw || typeof raw !== 'object') return { ...DEFAULT_SETTINGS };
  const r = raw as Partial<LapoSettings>;
  return {
    displayName: typeof r.displayName === 'string' ? r.displayName : DEFAULT_SETTINGS.displayName,
    theme:
      r.theme === 'light' || r.theme === 'dark' || r.theme === 'system' ? r.theme : DEFAULT_SETTINGS.theme,
    grayscale: typeof r.grayscale === 'boolean' ? r.grayscale : DEFAULT_SETTINGS.grayscale,
    customCss: typeof r.customCss === 'string' ? r.customCss : DEFAULT_SETTINGS.customCss,
  };
}
